import React from 'react';
import { Crown, Unlink, CheckCircle, AlertCircle, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '../../../ui/Button';
import { LinkedInIcon } from '../../../ui/LinkedInIcon';
import { LinkedInConnection } from '../../../../types';

interface LinkedInConnectionCardProps {
  connection: LinkedInConnection;
  isValidating: boolean;
  isDisconnecting: boolean;
  onValidate: () => void;
  onDisconnect: () => void;
  onReconnect: () => void;
}

/**
 * Card displaying a single LinkedIn connection with actions
 */
export const LinkedInConnectionCard: React.FC<LinkedInConnectionCardProps> = ({
  connection,
  isValidating,
  isDisconnecting,
  onValidate,
  onDisconnect,
  onReconnect,
}) => {
  const isExpired = connection.token_status === 'expired';
  const isExpiringSoon = connection.token_status === 'expiring_soon';
  const isPrimary = connection.is_primary;
  const isActive = connection.is_active;

  const getStatusColor = () => {
    if (isExpired || !isActive) return 'error';
    if (isExpiringSoon) return 'warning';
    return 'success';
  };

  const statusColor = getStatusColor();

  const getStatusText = () => {
    if (isExpired) return 'Token expired - needs reconnection';
    if (!isActive) return 'Disconnected';
    if (isExpiringSoon) return 'Token expiring soon';
    return 'Connected & active';
  };

  const bgClasses = {
    error: 'bg-error-50/80 border-error-100',
    warning: 'bg-warning-50/80 border-warning-100',
    success: 'bg-neutral-50/80 border-neutral-200',
  };

  const iconBgClasses = {
    error: 'bg-error-100',
    warning: 'bg-neutral-100',
    success: isPrimary ? 'bg-linkedin' : 'bg-neutral-100',
  };

  const iconColorClasses = {
    error: 'text-error-500',
    warning: 'text-neutral-500',
    success: isPrimary ? 'text-white' : 'text-neutral-500',
  };

  const dotClasses = {
    error: 'bg-error-500',
    warning: 'bg-warning-500',
    success: 'bg-success-500',
  };

  const textClasses = {
    error: 'text-error-500',
    warning: 'text-warning-600',
    success: 'text-success-600',
  };

  return (
    <div className={`group relative flex items-center gap-3 p-3 rounded-xl transition-all duration-200 border ${bgClasses[statusColor]}`}>
      {/* Desktop icon */}
      <div className="relative flex-shrink-0 hidden sm:block">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconBgClasses[statusColor]}`}>
          <LinkedInIcon size={16} className={iconColorClasses[statusColor]} />
        </div>
        <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${dotClasses[statusColor]}`} />
      </div>

      {/* Mobile dot */}
      <div className={`sm:hidden w-2 h-2 rounded-full flex-shrink-0 ${dotClasses[statusColor]}`} />

      {/* Info */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-neutral-900 truncate">
            {connection.display_name || connection.linkedin_email}
          </span>
          {isPrimary && isActive && !isExpired && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-2xs font-semibold rounded-full uppercase tracking-wide bg-linkedin text-white flex-shrink-0">
              <Crown className="w-2.5 h-2.5" />
              Primary
            </span>
          )}
        </div>
        <span className={`text-2xs font-medium block truncate ${textClasses[statusColor]}`}>
          {getStatusText()}
        </span>
        {connection.error_count > 0 && (
          <span className="text-2xs text-error-500 flex items-center gap-1 mt-0.5">
            <AlertCircle className="w-3 h-3" />
            {connection.error_count} error(s)
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {isActive && !isExpired ? (
          <>
            <Button
              id={`validate-linkedin-${connection.id}`}
              variant="secondary"
              size="sm"
              onClick={onValidate}
              disabled={isValidating}
              loading={isValidating}
              leftIcon={!isValidating ? <CheckCircle className="w-3 h-3" /> : undefined}
              className="text-2xs px-2 py-1"
            >
              Validate
            </Button>
            <Button
              id={`disconnect-linkedin-${connection.id}`}
              variant="secondary"
              size="sm"
              onClick={onDisconnect}
              disabled={isDisconnecting}
              loading={isDisconnecting}
              leftIcon={!isDisconnecting ? <Unlink className="w-3 h-3" /> : undefined}
              className="text-2xs px-2 py-1"
            >
              Disconnect
            </Button>
          </>
        ) : (
          <>
            <Button
              id={`reconnect-linkedin-${connection.id}`}
              variant="primary"
              size="sm"
              onClick={onReconnect}
              leftIcon={<RefreshCw className="w-3 h-3" />}
              className="text-2xs px-2 py-1"
            >
              Reconnect
            </Button>
            <Button
              id={`remove-linkedin-${connection.id}`}
              variant="secondary"
              size="sm"
              onClick={onDisconnect}
              disabled={isDisconnecting}
              loading={isDisconnecting}
              leftIcon={!isDisconnecting ? <Trash2 className="w-3 h-3" /> : undefined}
              className="text-2xs px-2 py-1"
            >
              Remove
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
